import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  Pressable,
  Image,
  ImageBackground,
  Dimensions,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

interface AccessRequestSuccessProps {
  navigate: (path: string) => void;
}

export const AccessRequestSuccess: React.FC<AccessRequestSuccessProps> = ({ navigate }) => {
  const [screenData, setScreenData] = React.useState(Dimensions.get('window'));
  const isWeb = Platform.OS === 'web';
  const isLargeScreen = isWeb && screenData.width >= 1024;

  React.useEffect(() => {
    if (isWeb) {
      const subscription = Dimensions.addEventListener('change', ({ window }) => {
        setScreenData(window);
      });
      
      return () => subscription?.remove();
    }
  }, [isWeb]);

  return (
    <SafeAreaView style={styles.container}>
      <View style={[
        styles.content, 
        isWeb && { height: screenData.height },
        isLargeScreen ? styles.horizontalLayout : styles.verticalLayout
      ]}>
        <View style={[
          styles.imageSection,
          isLargeScreen ? styles.imageSectionHorizontal : styles.imageSectionVertical
        ]}>
          <ImageBackground
            source={require('../assets/images/datacenter-background.png')}
            style={styles.backgroundImage}
            resizeMode="cover"
          >
            <View style={styles.overlay} />
          </ImageBackground>
        </View>
        {isLargeScreen ? (
          <View style={styles.contentSectionHorizontal}>
            <View style={styles.contentCard}>
              {/* Success Icon */}
              <View style={styles.iconContainer}>
                <View style={styles.successIcon}>
                  <Text style={styles.checkmark}>✓</Text>
                </View>
              </View>

              {/* Success Title */}
              <Text style={styles.title}>Access Request Submitted Successfully</Text>

              {/* Success Message */}
              <Text style={styles.message}>
                Your request for application access has been received. You'll be notified once it's reviewed.
              </Text>

              {/* Awaiting Approval Button */}
              <Pressable style={styles.awaitingButton} onPress={() => navigate('/')}>
                <Text style={styles.awaitingButtonText}>Awaiting For Approval</Text>
              </Pressable>
            </View>
          </View>
        ) : (
          <View style={styles.contentSectionVertical}>
            <View style={styles.contentCardVertical}>
              {/* Success Icon */}
              <View style={styles.iconContainer}>
                <View style={styles.successIcon}>
                  <Text style={styles.checkmark}>✓</Text>
                </View>
              </View>

              {/* Success Title */}
              <Text style={styles.title}>Access Request Submitted Successfully</Text>

              {/* Success Message */}
              <Text style={styles.message}>
                Your request for application access has been received. You'll be notified once it's reviewed.
              </Text>

              {/* Awaiting Approval Button */}
              <Pressable style={styles.awaitingButton} onPress={() => navigate('/')}>
                <Text style={styles.awaitingButtonText}>Awaiting For Approval</Text>
              </Pressable>
            </View>
          </View>
        )}
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#ffffff',
    overflow: 'hidden',
  },
  content: {
    flex: 1,
    overflow: 'hidden',
  },
  horizontalLayout: {
    flexDirection: 'row',
  },
  verticalLayout: {
    flexDirection: 'column',
    ...Platform.select({
      web: {
        position: 'relative',
      },
    }),
  },
  imageSection: {
    backgroundColor: '#f0f0f0',
  },
  imageSectionHorizontal: {
    flex: 1,
  },
  imageSectionVertical: {
    height: 300,
    ...Platform.select({
      web: {
        height: '40vh' as any,
        minHeight: 250,
      },
    }),
  },
  backgroundImage: {
    flex: 1,
    width: '100%',
    height: '100%',
  },
  overlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
  },
  contentSectionHorizontal: {
    flex: 1,
    paddingHorizontal: 60,
    paddingVertical: 40,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
  },
  contentSectionVertical: {
    paddingHorizontal: 24,
    zIndex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
    ...Platform.select({
      ios: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        paddingVertical: 40,
      },
      android: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        paddingVertical: 30,
      },
      web: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        paddingHorizontal: 32,
        paddingVertical: 40,
      },
    }),
  },
  contentCard: {
    width: '100%',
    maxWidth: 440,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
    backgroundColor: '#fff',
    borderRadius: 8,
    overflow: 'hidden',
  },
  contentCardVertical: {
    backgroundColor: '#ffffff',
    borderRadius: 16,
    paddingHorizontal: 24,
    paddingVertical: 32,
    marginHorizontal: 0,
    maxWidth: 400,
    width: '100%',
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
    ...Platform.select({
      ios: {
        paddingVertical: 40,
        shadowColor: '#000',
        shadowOffset: {
          width: 0,
          height: 4,
        },
        shadowOpacity: 0.1,
        shadowRadius: 12,
      },
      android: {
        paddingVertical: 36,
        elevation: 8,
      },
      web: {
        boxShadow: '0 4px 24px rgba(0, 0, 0, 0.1)',
        paddingHorizontal: 32,
        paddingVertical: 40,
      },
    }),
  },
  iconContainer: {
    marginBottom: 24,
    alignItems: 'center',
  },
  successIcon: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: '#4CAF50',
    alignItems: 'center',
    justifyContent: 'center',
    ...Platform.select({
      web: {
        boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
      },
      android: {
        elevation: 8,
      },
      ios: {
        shadowColor: '#4CAF50',
        shadowOffset: {
          width: 0,
          height: 4,
        },
        shadowOpacity: 0.3,
        shadowRadius: 8,
      },
    }),
  },
  checkmark: {
    fontSize: 32,
    color: '#fff',
    fontWeight: 'bold',
  },
  title: {
    fontSize: 20,
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: 16,
    color: '#1a1a1a',
    lineHeight: 28,
  },
  message: {
    fontSize: 14,
    color: '#333',
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 32,
  },
  awaitingButton: {
    backgroundColor: '#f5f5f5',
    borderRadius: 6,
    paddingVertical: 12,
    paddingHorizontal: 24,
    alignItems: 'center',
    width: '100%',
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  awaitingButtonText: {
    color: '#666',
    fontSize: 14,
    fontWeight: '500',
  },
});
